import type { ReportReadVariant, ReportReadVerdict } from 'definitions/report-read';

/**
 * What the result strip on screen 1 says, and the small facts the report card is labelled with,
 * as plain functions so the copy is tested without rendering anything. What the card itself
 * says lives in report-summary.ts.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

type RunDaysParams = { startsAt: number | null; endsAt: number | null };

/** Whole days the experiment ran, at least one; 0 when it has no recorded start or end. */
export const runDays = ({ startsAt, endsAt }: RunDaysParams): number =>
  startsAt === null || endsAt === null ? 0 : Math.max(1, Math.round((endsAt - startsAt) / DAY_MS));

/** The device tag: what the experiment targeted, 'all devices' when it did not narrow it. */
export const deviceTag = (targeting: readonly { device: string | null }[]): string => {
  const devices = targeting.map((row) => row.device).filter((device): device is string => !!device);
  if (devices.length === 0 || devices.includes('All')) return 'all devices';
  return devices.map((device) => device.toLowerCase()).join(' · ');
};

/** 'https://www.shop.se/checkout' becomes 'shop.se', for the card's note. Empty when unparseable. */
export const websiteLabel = (url: string | null | undefined): string => {
  if (!url) return '';
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

/** '3.12%'. A dash for no sessions. */
export const formatRate = (rate: number | null): string => (rate === null ? '-' : `${(rate * 100).toFixed(2)}%`);

/** The lift cell: signed to one decimal with a true minus, plus whether it is significant. */
export const formatDelta = ({ isControl, improvement, significant }: ReportReadVariant): string => {
  if (isControl) return 'baseline';
  if (improvement === null) return 'no data';
  const sign = improvement < 0 ? '−' : '+';
  return `${sign}${Math.abs(improvement).toFixed(1)}% · ${significant ? 'sig.' : 'n.s.'}`;
};

const formatP = (pValue: number | null): string => {
  if (pValue === null) return '';
  if (pValue < 0.001) return ' (p < 0.001)';
  return ` (p = ${pValue.toFixed(pValue < 0.01 ? 3 : 2)})`;
};

/** How many variants were compared against the control. */
export const challengerCount = (variants: readonly ReportReadVariant[]): number =>
  variants.filter((variant) => !variant.isControl).length;

/**
 * A result that looks real on the raw p-value but did not clear the corrected bar.
 *
 * The stored p-value is per variant and uncorrected; whether a result counts as significant is
 * decided after the multiple-comparison correction (Holm-Bonferroni by default), which asks more
 * of each variant the more are compared. With two challengers the best raw p must be under
 * 0.025, so a p of 0.03 reads as 97% confidence and is still not significant. Saying "no effect"
 * for that, beside a 97%, contradicts the number; saying nothing about the correction hides why.
 */
export const isNearMiss = ({ kind, pValue }: ReportReadVerdict): boolean =>
  kind === 'inconclusive' && pValue !== null && pValue < 0.05;

/** ' once 3 variants are compared', or nothing when there is only one comparison to make. */
export const comparedClause = (challengers: number): string =>
  challengers > 1 ? ` once ${challengers} variants are compared` : '';

/** The sentence under the result table. The tone follows the verdict kind. */
export const verdictSentence = (verdict: ReportReadVerdict, challengers = 1): string => {
  const { kind, variantName, pValue } = verdict;
  if (kind === 'winner') return `${variantName ?? 'A variant'} beat the control${formatP(pValue)}.`;
  if (kind === 'loser') return `${variantName ?? 'A variant'} did worse than the control${formatP(pValue)}.`;
  if (isNearMiss(verdict)) {
    return `The best result${formatP(pValue)} did not clear the bar${comparedClause(challengers)}.`;
  }
  return `No variant beat the control with confidence${formatP(pValue)}.`;
};

/** Bar width as a share of the best rate, so the longest bar is full and none vanishes. */
export const barWidth = (rate: number | null, variants: readonly ReportReadVariant[]): number => {
  const best = Math.max(0, ...variants.map((variant) => variant.rate ?? 0));
  if (rate === null || best === 0) return 0;
  return Math.max(4, Math.round((rate / best) * 100));
};
